import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface Tenant {
  id: string
  name: string
  slug: string // URL-friendly company name
  uniqueKey: string // Unguessable random key for URL security
  domain: string
  netlifySiteId?: string
  createdAt: string
  updatedAt: string
  settings?: {
    customDomain?: string
    branchPrefix?: string
    primaryColor?: string
    logo?: string
  }
}

interface TenantContextType {
  currentTenant: Tenant | null
  setCurrentTenant: (tenant: Tenant | null) => void
  userTenants: Tenant[]
  setUserTenants: (tenants: Tenant[]) => void
  loading: boolean
}

const TenantContext = createContext<TenantContextType | undefined>(undefined)

export const useTenant = () => {
  const context = useContext(TenantContext)
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider')
  }
  return context
}

interface TenantProviderProps {
  children: ReactNode
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [currentTenant, setCurrentTenantState] = useState<Tenant | null>(null)
  const [userTenants, setUserTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [authToken, setAuthToken] = useState<string | null>(localStorage.getItem('auth-token'))

  useEffect(() => {
    // SECURITY FIX: Track mounted state to prevent state updates on unmounted component
    let isMounted = true

    const initializeTenant = async () => {
      try {
        await loadTenant(isMounted)
      } catch (error) {
        console.error('Failed to initialize tenant:', error)
        // Only update state if component is still mounted
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    initializeTenant()

    // Watch for auth token changes in localStorage (handles login/logout)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth-token') {
        console.log('[TenantContext] Auth token changed, reloading tenant')
        setAuthToken(e.newValue)
        setLoading(true) // Reset loading state
        initializeTenant() // Reload tenant
      }
    }

    // Also listen for custom auth events (for same-window login)
    const handleAuthChange = () => {
      const newToken = localStorage.getItem('auth-token')
      console.log('[TenantContext] Auth change event detected, reloading tenant')
      setAuthToken(newToken)
      setLoading(true) // Reset loading state
      initializeTenant() // Reload tenant
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('auth-changed', handleAuthChange)

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('auth-changed', handleAuthChange)
    }
  }, [])

  const loadTenant = async (isMounted: boolean) => {
    try {
      // Skip tenant loading for platform admin build
      const isPlatformAdmin = import.meta.env.VITE_IS_PLATFORM_ADMIN === 'true'
      if (isPlatformAdmin) {
        console.log('🔐 Platform Admin build - skipping tenant lookup')
        if (isMounted) {
          setLoading(false)
        }
        return
      }

      // Strategy 0: Check if this is a branch deploy with pre-configured tenant
      // @ts-ignore - __TENANT_CONFIG__ is injected at build time for branch deploys
      if (typeof __TENANT_CONFIG__ !== 'undefined' && __TENANT_CONFIG__.tenantId && __TENANT_CONFIG__.version) {
        // @ts-ignore
        console.log('📦 Branch deploy detected:', __TENANT_CONFIG__)

        // Store tenant ID immediately, even before API call
        // @ts-ignore
        sessionStorage.setItem('tenant-id', __TENANT_CONFIG__.tenantId)
        // @ts-ignore
        sessionStorage.setItem('content-version', __TENANT_CONFIG__.version)
        // @ts-ignore
        console.log('✅ Stored tenant ID in sessionStorage (pre-fetch):', __TENANT_CONFIG__.tenantId)

        // Fetch tenant by ID
        // @ts-ignore
        const response = await fetch(`/api/tenants?id=${__TENANT_CONFIG__.tenantId}`)
        if (response.ok) {
          const tenant = await response.json()
          // Only update state if component is still mounted
          if (isMounted) {
            setCurrentTenantState(tenant)
            console.log('✅ Loaded tenant from API:', tenant)
            setLoading(false)
          }
          return
        } else {
          console.error('❌ Failed to fetch tenant from API:', response.status)
          // Still set loading to false so user can proceed (only if mounted)
          if (isMounted) {
            setLoading(false)
          }
          return
        }
      }

      // Strategy 1: Try to detect tenant from URL (for public access)
      const urlTenant = await detectTenantFromUrl()
      if (urlTenant && isMounted) {
        setCurrentTenantState(urlTenant)
        setLoading(false)
        return
      }

      // Strategy 2: Fall back to auth-based detection (for admin access)
      // Skip auth-based detection on marketing routes
      const pathname = window.location.pathname
      const isMarketingRoute = pathname === '/' || pathname.startsWith('/pricing') || pathname.startsWith('/directory')

      if (!isMarketingRoute) {
        await loadTenantFromAuth(isMounted)
      } else {
        // On marketing routes, just finish loading without auth check (only if mounted)
        if (isMounted) {
          setLoading(false)
        }
      }
    } catch (error) {
      console.error('Failed to load tenant:', error)
      // Only update state if component is still mounted
      if (isMounted) {
        setLoading(false)
      }
    }
  }

  const detectTenantFromUrl = async (): Promise<Tenant | null> => {
    try {
      const hostname = window.location.hostname
      const pathname = window.location.pathname

      // Skip URL-based detection for platform routes (marketing site, admin, etc.)
      const platformRoutes = ['/admin', '/login', '/pricing', '/directory', '/platformadmin']
      if (platformRoutes.some(route => pathname.startsWith(route)) || pathname === '/') {
        // Check if we're on the main platform site (not a tenant subdomain)
        const parts = hostname.split('.')
        const subdomain = parts[0]
        const isPlatformSite = ['www', 'app', 'admin', 'api'].includes(subdomain) || parts.length <= 2

        if (isPlatformSite) {
          return null
        }
      }

      // Strategy 1: Subdomain detection with version support
      // Format: {company}-{version}-{shortId}-{uniqueKey}.trustcenter.page
      // Example: acme-live-a83f92h4-s8dk38dk.trustcenter.page
      const parts = hostname.split('.')
      if (parts.length > 0) {
        const subdomain = parts[0]

        // Parse versioned URL format: company-version-shortId-uniqueKey
        const urlParts = subdomain.split('-')
        if (urlParts.length >= 4) {
          // Extract the last two parts as shortId and uniqueKey
          const uniqueKey = urlParts[urlParts.length - 1]
          const shortId = urlParts[urlParts.length - 2]
          const version = urlParts[urlParts.length - 3]
          const slug = urlParts.slice(0, urlParts.length - 3).join('-')

          // Validate version
          if (['live', 'preview', 'last'].includes(version)) {
            const result = await lookupTenantByKey(slug, shortId, uniqueKey)
            if (result) {
              // Store the version and tenant ID for use by content APIs
              sessionStorage.setItem('content-version', version)
              sessionStorage.setItem('tenant-id', result.id)
              return result
            }
          }
        }

        // Fallback to old format for backwards compatibility
        if (!['www', 'app', 'admin', 'api'].includes(subdomain)) {
          const tenant = await lookupTenantByIdentifier(subdomain)
          if (tenant) {
            sessionStorage.setItem('content-version', 'live') // Default to live
            sessionStorage.setItem('tenant-id', tenant.id)
            return tenant
          }
        }
      }

      // Strategy 2: Path-based detection
      // Example: /t/cloudsecurityprogram -> tenant = cloudsecurityprogram
      const pathMatch = pathname.match(/^\/t\/([^\/]+)/)
      if (pathMatch) {
        const tenantSlug = pathMatch[1]
        const tenant = await lookupTenantByIdentifier(tenantSlug)
        if (tenant) {
          sessionStorage.setItem('tenant-id', tenant.id)
          return tenant
        }
      }

      // Strategy 3: Custom domain lookup
      // Example: trust.cloudsecurity.com -> lookup tenant with customDomain
      const tenant = await lookupTenantByCustomDomain(hostname)
      if (tenant) {
        sessionStorage.setItem('tenant-id', tenant.id)
        return tenant
      }

      return null
    } catch (error) {
      console.error('Failed to detect tenant from URL:', error)
      return null
    }
  }

  const lookupTenantByKey = async (slug: string, shortId: string, uniqueKey: string): Promise<Tenant | null> => {
    try {
      const response = await fetch(`/api/tenants/lookup?slug=${encodeURIComponent(slug)}&shortId=${encodeURIComponent(shortId)}&uniqueKey=${encodeURIComponent(uniqueKey)}`)
      if (response.ok) {
        return await response.json()
      }
      return null
    } catch (error) {
      console.error('Failed to lookup tenant by key:', error)
      return null
    }
  }

  const lookupTenantByIdentifier = async (identifier: string): Promise<Tenant | null> => {
    try {
      const response = await fetch(`/api/tenants/lookup?identifier=${encodeURIComponent(identifier)}`)
      if (response.ok) {
        return await response.json()
      }
      return null
    } catch (error) {
      console.error('Failed to lookup tenant by identifier:', error)
      return null
    }
  }

  const lookupTenantByCustomDomain = async (domain: string): Promise<Tenant | null> => {
    try {
      const response = await fetch(`/api/tenants/lookup?domain=${encodeURIComponent(domain)}`)
      if (response.ok) {
        return await response.json()
      }
      return null
    } catch (error) {
      console.error('Failed to lookup tenant by custom domain:', error)
      return null
    }
  }

  const loadTenantFromAuth = async (isMounted: boolean) => {
    try {
      // Get authenticated user from session
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = { 'Content-Type': 'application/json' }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const authResponse = await fetch('/api/auth/validate', {
        method: 'GET',
        credentials: 'include',
        headers
      })

      if (!authResponse.ok) {
        console.warn(`[TenantContext] Auth validation failed with status ${authResponse.status}. User may not be logged in.`)
        // Only update state if component is still mounted
        if (isMounted) {
          setLoading(false)
        }
        return
      }

      const authData = await authResponse.json()
      const userEmail = authData.user?.email

      if (!userEmail) {
        console.warn('[TenantContext] No user email found in auth response. Cannot determine tenant.')
        // Only update state if component is still mounted
        if (isMounted) {
          setLoading(false)
        }
        return
      }

      // Extract domain from user's email
      const parts = userEmail.split('@')
      const userDomain = parts.length > 1 ? parts[1] : null
      console.log(`[TenantContext] Loading tenant for user domain: ${userDomain}`)

      // Find tenant for this domain
      const tenantsResponse = await fetch('/api/tenants')
      if (tenantsResponse.ok) {
        const tenants = await tenantsResponse.json()
        const userTenant = tenants.find((t: Tenant) => t.domain === userDomain)

        if (!userTenant) {
          console.warn(`[TenantContext] No tenant found for domain: ${userDomain}`)
        }

        // Only update state if component is still mounted
        if (userTenant && isMounted) {
          setCurrentTenantState(userTenant)
          setUserTenants([userTenant]) // User only has access to their own tenant
          console.log(`[TenantContext] Successfully loaded tenant: ${userTenant.name} (${userTenant.id})`)
        }
      } else {
        console.error(`[TenantContext] Failed to fetch tenants list. Status: ${tenantsResponse.status}`)
      }
    } catch (error) {
      console.error('[TenantContext] Critical error loading tenant from auth:', error)
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      console.error(`[TenantContext] Error details: ${errorMsg}`)
    } finally {
      // Only update state if component is still mounted
      if (isMounted) {
        setLoading(false)
      }
    }
  }

  const setCurrentTenant = (tenant: Tenant | null) => {
    // This is now a no-op - tenant is auto-determined, cannot be manually changed
    console.warn('Manual tenant switching is disabled. Tenant is auto-determined from user email.')
  }

  return (
    <TenantContext.Provider
      value={{
        currentTenant,
        setCurrentTenant,
        userTenants,
        setUserTenants,
        loading
      }}
    >
      {children}
    </TenantContext.Provider>
  )
}
